import prices from '@/data/price.json';
import countriesData from '@/data/countries.json';
import currenciesData from '@/data/currencies.json';

export const DEFAULT_PRICE_REGION = 'US';

const priceKeys = new Set(Object.keys(prices));

/** @param {string | undefined | null} cc */
export function normalizeCountryCode(cc) {
  if (!cc || typeof cc !== 'string') return DEFAULT_PRICE_REGION;
  return cc.trim().toUpperCase().slice(0, 2);
}

/**
 * Maps Vercel/geo country to a key that exists in price.json.
 */
export function resolvePriceRegion(detectedCountry) {
  const u = normalizeCountryCode(detectedCountry);
  if (priceKeys.has(u)) return u;
  return DEFAULT_PRICE_REGION;
}

/** @param {string} region */
export function getCurrencyForRegion(region) {
  const r = normalizeCountryCode(region);
  return countriesData[r]?.currency || countriesData[DEFAULT_PRICE_REGION]?.currency || 'USD';
}

/** @param {string} code ISO 4217 */
export function getCurrencyMeta(code) {
  const c = code || 'USD';
  return (
    currenciesData[c] ||
    currenciesData.USD || {
      code: 'USD',
      symbol: '$',
      decimals: 2,
      name: 'US Dollar',
    }
  );
}

/**
 * Resolves current pricing mode from environment variable.
 * @returns {'static' | 'dynamic'}
 */
export function getPricingMode() {
  return (process.env.NEXT_PUBLIC_PRICING_MODE || 'static').toLowerCase().trim();
}

/**
 * Build full geo + pricing payload for provider (server or client).
 * @param {string | undefined | null} detectedCountry — from Vercel / cookie
 */
export function buildGeoPricingState(detectedCountry) {
  const mode = getPricingMode();
  const isStatic = mode === 'static';

  const detected = isStatic ? 'US' : normalizeCountryCode(detectedCountry);
  const priceRegion = isStatic ? 'US' : resolvePriceRegion(detected);
  const amounts = prices[priceRegion] || prices.US;

  const currency = isStatic ? 'USD' : getCurrencyForRegion(priceRegion);
  const meta = getCurrencyMeta(currency);

  return {
    pricingMode: mode,
    detectedCountry: detected,
    priceRegion,
    currencyMeta: {
      code: meta.code,
      symbol: meta.symbol,
      decimals: meta.decimals,
      name: meta.name,
    },
    amounts,
  };
}

/**
 * Round/format an amount using currency decimals (for display).
 * @param {number} amount
 * @param {number} decimals
 */
export function formatMoney(amount, decimals) {
  if (amount == null || Number.isNaN(amount)) return '';
  const d = decimals ?? 2;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(amount);
}
